/**
 * The seniority taxonomy the LinkedIn enrichment assigns to a job title.
 *
 * Shared because the Lead Scoring rubric's JOB_LEVEL ladder is matched against
 * these exact strings: a value the ladder does not list scores nothing, so a
 * ladder that silently omits one under-scores every lead holding it. K2's ladder
 * omitted "C-Suite (excl. CEO, MD, Founder & Board)" — the seniority the
 * enrichment gives German executive titles (Betriebsleiter, Vorstand Technik,
 * Geschäftsleiter, Chief Operating Officer) — so those leads forfeited up to 3
 * points and landed at the lowest priority (PREC-3788).
 *
 * Keep in sync with the enrichment prompt in
 * `apps/api/src/baseLinkedinEnrichment/base-linkedin-ai-enrichment.service.ts`.
 */
export const SENIORITY_LEVELS = [
	"Intern/Student",
	"Entry-Level",
	"Mid-Level",
	"Senior",
	"Head of / Team Lead",
	"Director",
	"VP",
	"Partner",
	"C-Suite (excl. CEO, MD, Founder & Board)",
	"CEO/Founder/Owner/Board Member",
	"Unsure",
] as const;

export type SeniorityLevel = (typeof SENIORITY_LEVELS)[number];

/** The enrichment's explicit "could not classify" bucket. */
export const UNKNOWN_SENIORITY: SeniorityLevel = "Unsure";

/**
 * The seniority values a JOB_LEVEL ladder can actually score — everything the
 * enrichment assigns except its could-not-classify bucket.
 */
export const SCORABLE_SENIORITY_LEVELS: readonly SeniorityLevel[] = SENIORITY_LEVELS.filter(
	(level) => level !== UNKNOWN_SENIORITY
);

/** Comparison form — the ladder and the enrichment differ only in case/whitespace. */
export const normalizeSeniority = (value: string): string =>
	value.trim().toLowerCase().replace(/\s+/g, " ");

/** One graded bucket of a rubric's JOB_LEVEL ladder. */
export interface SeniorityLadderItem {
	seniorityLevel?: string[];
}

/**
 * The scorable seniority values this ladder never lists. A lead holding one of
 * them scores nothing for JOB_LEVEL, so an omission silently caps its priority —
 * surfaced in the rubric editor and asserted by the scoring engine's tests.
 */
export const uncoveredSeniorityLevels = (items: SeniorityLadderItem[] = []): string[] => {
	const covered = new Set(
		items.flatMap((item) => (item?.seniorityLevel ?? []).map(normalizeSeniority))
	);

	return SCORABLE_SENIORITY_LEVELS.filter((level) => !covered.has(normalizeSeniority(level)));
};
